/**
 * AnalysisPanel — renders a completed AnalysisResult as a retro "report".
 *
 * Layout (Stack spacing 3):
 *   Header row: SeverityBadge · action chip · confidence + cost · model caption
 *   Sources chip row (agentic — sources_used)
 *   ▸ SUMMARY heading → body
 *   ▸ IMPACT heading → body
 *   ▸ SUGGESTED FIX heading → JB Mono fenced block
 *   [▼ REPL] RetroAccordion → ReplStream of tool_calls_log (agentic only)
 *   Related services (chip row)
 *
 * #9: The old "▸ RECON LOG" flat list is replaced by a RetroAccordion wrapping
 * a ReplStream, giving parity with InvestigationDetailPage's REPL section.
 */

import { Box, Chip, Stack, Typography } from '@mui/material';

import type { AnalysisResult, ToolCallEntry } from '@/types/analysis';
import { SeverityBadge } from '@/components/SeverityBadge';
import { RetroAccordion } from '@/components/RetroAccordion';
import { ReplStream, adaptToolCallsToTimeline } from '@/components/ReplStream';
import type { TimelineEntry } from '@/components/InvestigationTimeline';
import { TraceTreeSection } from '@/components/TraceTreeSection';
import { RecentEventsSection } from '@/components/RecentEventsSection';
import { LogSummarySection } from '@/components/LogSummarySection';

// Convert ToolCallEntry[] (analysis type, result_preview is always a string)
// to TimelineEntry[] (investigation type, result_preview is string | null).
function toolCallsToTimeline(entries: ToolCallEntry[]): TimelineEntry[] {
  return entries.map((e, idx) => ({
    call_id: `r${e.round}-${idx}`,
    round: e.round,
    tool: e.tool,
    args: e.args,
    result_preview: e.result_preview,
    latency_ms: e.latency_ms,
    error: e.error,
  }));
}

// ---------------------------------------------------------------------------
// Action display helpers
// ---------------------------------------------------------------------------

const ACTION_LABELS: Record<AnalysisResult['action'], string> = {
  do_now: 'Do now',
  investigate: 'Investigate',
  watch: 'Watch',
  wontfix: "Won't fix",
};

type ActionChipColor = 'error' | 'warning' | 'info' | 'default';

function actionChipColor(action: AnalysisResult['action']): ActionChipColor {
  switch (action) {
    case 'do_now':
      return 'error';
    case 'investigate':
      return 'warning';
    case 'watch':
      return 'info';
    case 'wontfix':
      return 'default';
  }
}

// ---------------------------------------------------------------------------
// Cost formatter — handles $0.00 correctly when tokens are 0 (Agent SDK)
// ---------------------------------------------------------------------------

function formatCost(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

// ---------------------------------------------------------------------------
// Source label normaliser — strip the dot-separated suffix for display
// e.g. 'sentry.event' → 'sentry', 'github.read_file' → 'github'
// ---------------------------------------------------------------------------

function sourceShortLabel(source: string): string {
  return source.split('.')[0] ?? source;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface AnalysisPanelProps {
  result: AnalysisResult;
}

export function AnalysisPanel({ result }: AnalysisPanelProps) {
  const confidencePct = Math.round(result.confidence * 100);

  // Deduplicate source short-labels for the chip row
  const uniqueSources = Array.from(
    new Set((result.sources_used ?? []).map(sourceShortLabel)),
  );

  const hasResearchLog = (result.tool_calls_log ?? []).length > 0;
  const rounds = result.rounds ?? 0;

  return (
    <Stack spacing={3}>
      {/* ---- Header row ---- */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <SeverityBadge severity={result.severity} score={result.severity_score} />
        <Chip
          size="small"
          label={ACTION_LABELS[result.action]}
          color={actionChipColor(result.action)}
        />
        <Typography variant="caption" color="text.secondary">
          {confidencePct}% confidence
        </Typography>
        <Typography variant="caption" color="text.secondary">
          analysed by {result.model} · {formatCost(result.cost_usd)} · {result.latency_ms}ms
          {rounds > 0 && ` · ${rounds} round${rounds !== 1 ? 's' : ''}`}
        </Typography>
      </Stack>

      {/* ---- Sources chip row (agentic only) ---- */}
      {uniqueSources.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            sources
          </Typography>
          {uniqueSources.map((src) => (
            <Chip
              key={src}
              label={src}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: 11 }}
            />
          ))}
        </Stack>
      )}

      {/* ---- ▸ SUMMARY ---- */}
      <Box>
        <Typography
          component="h3"
          sx={{
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            fontSize: '0.8125rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box component="span" sx={{ color: 'primary.main' }}>▸</Box>
          <Box component="span" sx={{ color: 'text.primary' }}>SUMMARY</Box>
        </Typography>
        <Typography variant="body1">{result.summary}</Typography>
      </Box>

      {/* ---- ▸ IMPACT ---- */}
      <Box>
        <Typography
          component="h3"
          sx={{
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            fontSize: '0.8125rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box component="span" sx={{ color: 'primary.main' }}>▸</Box>
          <Box component="span" sx={{ color: 'text.primary' }}>IMPACT</Box>
        </Typography>
        <Typography variant="body1">{result.impact}</Typography>
      </Box>

      {/* ---- ▸ SUGGESTED FIX ---- */}
      <Box>
        <Typography
          component="h3"
          sx={{
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            fontSize: '0.8125rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box component="span" sx={{ color: 'primary.main' }}>▸</Box>
          <Box component="span" sx={{ color: 'text.primary' }}>SUGGESTED FIX</Box>
        </Typography>
        {result.fix_snippet ? (
          <Box
            component="pre"
            sx={{
              bgcolor: 'background.elev2',
              color: 'text.primary',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: 13,
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              m: 0,
              lineHeight: 1.6,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {result.fix_snippet}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No code snippet for this issue.
          </Typography>
        )}
      </Box>

      {/* ---- Related services ---- */}
      {result.related_services.length > 0 && (
        <Box>
          <Typography
            component="h3"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.8125rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <Box component="span" sx={{ color: 'primary.main' }}>▸</Box>
            <Box component="span" sx={{ color: 'text.primary' }}>RELATED SERVICES</Box>
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {result.related_services.map((svc) => (
              <Chip key={svc} label={svc} size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}

      {/* ---- Structured tool context (Sprint 5) ---- */}
      {result.context?.trace?.result && (
        <TraceTreeSection trace={result.context.trace.result} />
      )}
      {result.context?.recent_events?.result && (
        <RecentEventsSection events={result.context.recent_events.result} />
      )}
      {result.context?.log_summary?.result && (
        <LogSummarySection summary={result.context.log_summary.result} />
      )}

      {/* ---- REPL (agentic only) — replaces the old flat RECON LOG (#9) ---- */}
      {hasResearchLog && (
        <RetroAccordion title="REPL" defaultOpen={true}>
          <Box sx={{ pt: 1, pb: 1 }}>
            <ReplStream
              prompt={result.summary ?? ''}
              anchor={null}
              timeline={adaptToolCallsToTimeline(toolCallsToTimeline(result.tool_calls_log))}
              narrative=""
              status="done"
            />
          </Box>
        </RetroAccordion>
      )}
    </Stack>
  );
}
